"use client";

import { useState } from "react";
import type { NewsletterSignupBlock } from "@/payload-types";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { getBackgroundColour, isDarkBackground } from "@/lib/colors";
import { CSSFadeUpIn } from "@/components/animations/CSSFadeUpIn";
import { HONEYPOT_FIELD } from "@/lib/honeypot";
import { pushDataLayer } from "@/lib/gtm";

interface NewsletterSignupRendererProps extends NewsletterSignupBlock {
  hasBackgroundImage?: boolean;
}

export const NewsletterSignupRenderer = (props: NewsletterSignupRendererProps) => {
  const {
    heading,
    description,
    inputPlaceholder = "Enter your email",
    buttonText = "Subscribe",
    backgroundColour = "black",
    hasBackgroundImage,
  } = props;

  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bgColor = getBackgroundColour(backgroundColour);
  const isDark = isDarkBackground(backgroundColour, hasBackgroundImage);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Reset states
    setError(null);
    setSuccess(false);

    // Client-side validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch("/api/newsletter-signup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          source: typeof window !== "undefined" ? window.location.pathname : undefined,
          [HONEYPOT_FIELD]: honeypot,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        setEmail(""); // Clear form
        setError(null);

        // Only a genuine new subscriber returns a real `id` — the
        // already-subscribed response omits it and the honeypot path returns
        // "blocked". Both come back 200, so counting them as conversions
        // would inflate the number in GA4.
        if (data.id && data.id !== "blocked") {
          pushDataLayer("newsletter_signup", {
            source: window.location.pathname,
          });
        }
      } else if (response.status === 429) {
        setError("Too many requests. Please try again in a few minutes.");
      } else if (data.alreadyExists) {
        // Already subscribed - still show success message
        setSuccess(true);
        setEmail("");
      } else {
        setError(data.error || "Oops! Something went wrong. Please try again.");
      }
    } catch {
      setError("Oops! Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CSSFadeUpIn>
      <div className={cn("w-full rounded-xl", bgColor)}>
        <div className="mx-auto max-w-[85rem] px-4 sm:px-6 lg:px-8">
          <div
            className={cn(
              "w-full text-left py-8 md:py-12",
              "flex flex-col md:flex-row md:items-center md:justify-between gap-6 md:gap-8"
            )}
          >
            {/* Left: Heading + Description */}
            <div className="flex-1 flex flex-col md:flex-row md:items-end gap-4 md:gap-6">
              {heading && (
                <h2
                  className={cn(
                    "font-subheading text-2xl md:text-3xl lg:text-4xl 2xl:text-5xl md:flex-shrink-0",
                    isDark ? "text-white" : "text-brand-dark"
                  )}
                >
                  {heading}
                </h2>
              )}
              {description && (
                <p
                  className={cn(
                    "text-base lg:text-lg",
                    isDark ? "text-white/90" : "text-brand-dark/90"
                  )}
                >
                  {description}
                </p>
              )}
            </div>

            {/* Right: Form (Email + Button inline) */}
            <form onSubmit={handleSubmit} className="flex-shrink-0 w-full md:w-auto">
              {/* Honeypot field - hidden from humans, bots fill it */}
              <input
                type="text"
                name={HONEYPOT_FIELD}
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                className="absolute -left-[9999px] opacity-0 h-0 w-0"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />

              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={inputPlaceholder || undefined}
                  disabled={loading || success}
                  className={cn(
                    "flex-1 md:min-w-[250px] px-4 py-2 rounded-md transition-colors",
                    "focus:outline-none focus:ring-2 focus:ring-offset-2",
                    isDark
                      ? "bg-white text-brand-dark placeholder:text-gray-400 focus:ring-white"
                      : "bg-white border border-brand-dark/20 text-brand-dark focus:ring-brand-dark"
                  )}
                  required
                />
                <Button
                  type="submit"
                  disabled={loading || success}
                  className={cn(
                    "min-w-[120px]",
                    isDark
                      ? "bg-white text-brand-dark hover:bg-white/90"
                      : "bg-brand-dark text-white hover:bg-brand"
                  )}
                >
                  {loading ? "..." : success ? "✓" : buttonText}
                </Button>
              </div>

              {/* Success/Error messages below form */}
              {success && (
                <p className={cn("text-sm mt-2", isDark ? "text-white" : "text-brand-dark")}>
                  Thanks for subscribing! We&apos;ll keep you updated.
                </p>
              )}
              {error && (
                <p className="text-sm mt-2 text-red-600">
                  {error}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </CSSFadeUpIn>
  );
};
